"use client"

import { useEffect, useRef, useState } from "react"
import Link from "next/link"
import { X, Trash2, ShoppingCart, Loader2, Download, Truck } from "lucide-react"
import { type CartLine, useCart } from "@/context/cart-context"
import { Button } from "@/components/ui/button"
import Image from "next/image"
import { toast } from "sonner"
import { useSettings } from "@/components/branding-provider"
import { formatPrintProductLabel, getPrintFitStatus, type PrintProduct } from "@/lib/print-catalog"
import { getSelectionFavoriteToken, getSelectionShareToken } from "@/lib/selection-policy"
import { trackStoreEvent } from "@/components/store-analytics-tracker"

interface CartSheetProps {
  isOpen: boolean
  onClose: () => void
}

export function CartSheet({ isOpen, onClose }: CartSheetProps) {
  const { cart, removeFromCart, cartTotal, cartCount, updatePrintQuantity, getPrintProduct } = useCart()
  const settings = useSettings()
  const [isCheckingOut, setIsCheckingOut] = useState(false)
  const trackedOpenRef = useRef(false)
  const priceInCents = settings.commerce?.highResPriceCents ?? 1000
  const activePromotion = settings.commerce?.promotionBanner?.enabled && settings.commerce.promotionBanner.code
    ? settings.commerce.promotionBanner
    : null
  const printSalesAvailable = Boolean(
    settings.commerce?.prints?.enabled && settings.commerce.prints.catalog.some(product => product.active)
  )
  const hasPrintItems = printSalesAvailable && cart.some(item => item.kind === 'print')
  const promoRemainingItems = activePromotion && activePromotion.minimumQuantity > cartCount
    ? activePromotion.minimumQuantity - cartCount
    : 0

  useEffect(() => {
    if (!isOpen) {
      trackedOpenRef.current = false
      return
    }

    if (trackedOpenRef.current) return
    trackedOpenRef.current = true
    trackStoreEvent({
      eventType: 'cart_open',
      galleryPath: getGalleryPathFromFilename(cart[0]?.filename),
      filename: cart[0]?.filename ?? null,
      amountTotal: cartTotal,
      currency: 'usd',
      metadata: {
        itemCount: cartCount,
        hasPrintItems,
        hasDigitalItems: cart.some(item => item.kind === 'digital'),
      },
    })
  }, [cart, cartCount, cartTotal, hasPrintItems, isOpen])

  if (!isOpen) return null

  const handleCheckout = async () => {
    setIsCheckingOut(true)
    try {
      const response = await fetch('/api/checkout', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          items: cart.map(item => {
            // Each line proves access to its own photo (private gallery link or saved list).
            const access = {
              shareToken: getSelectionShareToken(item.photo),
              favoriteToken: getSelectionFavoriteToken(item.photo),
            }
            if (item.kind === 'print') {
              return {
                kind: 'print',
                filename: item.filename,
                productId: item.productId,
                quantity: item.quantity,
                ...access,
              }
            }

            return {
              kind: 'digital',
              filename: item.filename,
              ...access,
            }
          }),
          returnUrl: `${window.location.origin}/checkout/success${window.location.search}`,
          cancelUrl: window.location.href,
        }),
      });

      if (response.ok) {
        const { url } = await response.json();
        window.location.href = url;
      } else {
        const message = await response.text()
        console.error('Checkout error:', message);
        toast.error(message || "Checkout is unavailable right now. Please try again or contact Jessica.")
        setIsCheckingOut(false)
      }
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error("Checkout is unavailable right now. Please try again or contact Jessica.")
      setIsCheckingOut(false)
    }
  };

  return (
    <div className="fixed inset-0 isolate z-[10000] flex justify-end">
      {/* Backdrop */}
      <div
        className="absolute inset-0 animate-in fade-in bg-black/60 backdrop-blur-sm duration-300"
        onClick={onClose}
      />

      {/* Sheet */}
      <div className="relative z-[10000] flex h-[100dvh] w-full max-w-md flex-col border-l bg-background shadow-2xl animate-in slide-in-from-right duration-300">
        <div className="z-10 flex flex-none items-center justify-between border-b bg-background/95 p-5 shadow-sm backdrop-blur">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-primary">Proofing cart</p>
            <h2 className="mt-1 flex items-center gap-2 text-lg font-semibold">
              <ShoppingCart className="h-5 w-5" />
              {cartCount} {cartCount === 1 ? 'item' : 'items'}
            </h2>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} className="min-h-11 min-w-11 rounded-full hover:bg-muted">
            <X className="h-5 w-5" />
          </Button>
        </div>

        <div className="flex flex-1 flex-col overflow-y-auto bg-background p-5">
          {cart.length === 0 ? (
            <div className="flex h-full flex-grow flex-col items-center justify-center space-y-4 rounded-2xl border border-dashed bg-muted/20 p-8 text-muted-foreground">
              <div className="shrink-0 rounded-2xl bg-primary/10 p-5 text-primary">
                <ShoppingCart className="h-10 w-10 opacity-50" />
              </div>
              <div className="shrink-0 text-center">
                <p className="font-medium text-foreground">Your cart is empty</p>
                <p className="mt-1 text-sm">
                  {printSalesAvailable
                    ? 'Add paid high-res downloads or print products from a gallery to get started.'
                    : 'Add paid high-res downloads from a gallery to get started.'}
                </p>
              </div>
              <Button variant="outline" onClick={onClose} className="min-h-11 shrink-0">
                Continue browsing
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {cart.map((photo) => (
                <CartLineItem
                  key={photo.id}
                  line={photo}
                  unitPriceCents={photo.kind === 'digital' ? priceInCents : getPrintProduct(photo.productId)?.priceCents ?? 0}
                  product={photo.kind === 'print' ? getPrintProduct(photo.productId) : null}
                  onRemove={() => removeFromCart(photo.id)}
                  onQuantityChange={(quantity) => updatePrintQuantity(photo.id, quantity)}
                />
              ))}
            </div>
          )}
        </div>

        <div className="z-10 flex-none space-y-4 border-t bg-background/95 p-5 backdrop-blur">
          <div className="rounded-2xl border bg-muted/30 p-3 text-sm text-muted-foreground">
            <div className="flex gap-3">
              <Download className="mt-0.5 h-4 w-4 flex-none text-primary" />
              <p>
                Purchased high-resolution files are delivered on your receipt page and by email. Social-ready gallery downloads remain available from eligible galleries.{' '}
                <Link href="/policies" className="font-medium text-foreground underline underline-offset-2">Review purchase terms</Link>.
              </p>
            </div>
            {hasPrintItems ? (
              <div className="mt-3 flex gap-3 border-t pt-3">
                <Truck className="mt-0.5 h-4 w-4 flex-none text-primary" />
                <p>Print orders include shipping at checkout and are reviewed before production.</p>
              </div>
            ) : null}
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Subtotal</span>
              <span>${(cartTotal / 100).toFixed(2)}</span>
            </div>
            {activePromotion ? (
              <div className="rounded-xl border bg-primary/10 px-3 py-2 text-xs leading-5 text-muted-foreground">
                Promo code <span className="font-mono font-semibold text-foreground">{activePromotion.code}</span> can be entered at checkout
                {activePromotion.minimumQuantity > 1 ? ` for ${activePromotion.minimumQuantity}+ items` : ''}.
                {promoRemainingItems > 0 ? (
                  <span className="block text-amber-700 dark:text-amber-300">
                    Add {promoRemainingItems} more {promoRemainingItems === 1 ? 'item' : 'items'} to qualify.
                  </span>
                ) : activePromotion.minimumQuantity > 1 ? (
                  <span className="block text-emerald-700 dark:text-emerald-300">
                    This cart qualifies for the promotion.
                  </span>
                ) : null}
              </div>
            ) : null}
            <div className="flex items-center justify-between text-lg font-bold">
              <span>Total</span>
              <span>${(cartTotal / 100).toFixed(2)}</span>
            </div>
          </div>
          <Button
            className="min-h-12 w-full"
            size="lg"
            disabled={cart.length === 0 || isCheckingOut}
            onClick={handleCheckout}
          >
            {isCheckingOut ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Processing...
              </>
            ) : (
              "Proceed to Checkout"
            )}
          </Button>
        </div>
      </div>
    </div>
  )
}

function formatCurrency(cents: number) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(cents / 100)
}

function getGalleryPathFromFilename(filename?: string | null) {
  if (!filename) return null
  const parts = filename.split('/').filter(Boolean)
  return parts.length > 1 ? parts.slice(0, -1).join('/') : null
}

function CartLineItem({
  line,
  unitPriceCents,
  product,
  onRemove,
  onQuantityChange,
}: {
  line: CartLine
  unitPriceCents: number
  product: PrintProduct | null
  onRemove: () => void
  onQuantityChange: (quantity: number) => void
}) {
  const title = line.photo.filename.split('/').pop() || line.filename
  const isPrint = line.kind === 'print'
  const printFitStatus = isPrint && product ? getPrintFitStatus(line.photo, product) : null

  return (
    <div className="group flex gap-4 rounded-2xl border bg-card p-3 shadow-sm">
      <div className="relative h-24 w-24 flex-shrink-0 overflow-hidden rounded-xl border bg-muted">
        <Image
          src={line.photo.thumbnailSrc || line.photo.src}
          alt={line.photo.filename}
          fill
          unoptimized
          className="object-cover transition-transform group-hover:scale-105"
        />
      </div>
      <div className="flex min-w-0 flex-1 flex-col justify-between py-1">
        <div className="space-y-1">
          <p className="line-clamp-2 font-medium leading-tight" title={line.photo.filename}>
            {title}
          </p>
          <p className="text-sm text-muted-foreground">
            {isPrint && product ? `${formatPrintProductLabel(product)} print` : 'Paid high-res digital file'}
          </p>
          <p className="text-xs leading-5 text-muted-foreground">
            {isPrint ? 'Reviewed before production; shipping is added at checkout.' : 'Delivered from your receipt page and confirmation email.'}
          </p>
          {printFitStatus ? (
            <p className="text-xs leading-5 text-muted-foreground">
              {printFitStatus.message}
            </p>
          ) : null}
        </div>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-3">
            <p className="font-semibold">{formatCurrency(unitPriceCents * line.quantity)}</p>
            {isPrint ? (
              <div className="inline-flex items-center overflow-hidden rounded-full border bg-background">
                <button
                  type="button"
                  onClick={() => onQuantityChange(line.quantity - 1)}
                  className="h-8 w-8 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  aria-label={`Decrease quantity for ${title}`}
                >
                  -
                </button>
                <span className="min-w-8 px-2 text-center text-xs font-semibold">{line.quantity}</span>
                <button
                  type="button"
                  onClick={() => onQuantityChange(line.quantity + 1)}
                  className="h-8 w-8 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  aria-label={`Increase quantity for ${title}`}
                >
                  +
                </button>
              </div>
            ) : null}
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="h-8 px-2 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
            onClick={onRemove}
          >
            <Trash2 className="mr-2 h-3 w-3" />
            Remove
          </Button>
        </div>
      </div>
    </div>
  )
}